import { supabase } from '@/lib/supabase';
import type { ProviderGamification, UnlockedBadge } from '../types/gamification.types';
import { DEFAULT_GAMIFICATION } from '../types/gamification.types';

export const ProviderGamificationService = {
  async getGamification(userId: string): Promise<ProviderGamification> {
    const { data, error } = await supabase
      .from('profiles')
      .select('gamification')
      .eq('id', userId)
      .single();

    if (error) throw error;

    const raw = data?.gamification as ProviderGamification | null;
    return raw ?? DEFAULT_GAMIFICATION;
  },

  async unlockBadge(userId: string, badgeId: string): Promise<boolean> {
    // Read current state
    const current = await this.getGamification(userId);

    // Idempotent: if already unlocked, skip
    if (current.badges.some((b) => b.id === badgeId)) {
      return false;
    }

    const newBadge: UnlockedBadge = {
      id: badgeId,
      unlocked_at: new Date().toISOString(),
    };

    const updated: ProviderGamification = {
      ...current,
      badges: [...current.badges, newBadge],
    };

    const { error } = await supabase
      .from('profiles')
      .update({ gamification: updated })
      .eq('id', userId);

    if (error) throw error;
    return true;
  },

  async markBadgesAsShown(userId: string, badgeIds: string[]): Promise<void> {
    const current = await this.getGamification(userId);

    const newShown = Array.from(new Set([...current.shown_badges, ...badgeIds]));

    const updated: ProviderGamification = {
      ...current,
      shown_badges: newShown,
    };

    const { error } = await supabase
      .from('profiles')
      .update({ gamification: updated })
      .eq('id', userId);

    if (error) throw error;
  },

  getUnshownBadges(state: ProviderGamification): UnlockedBadge[] {
    return state.badges.filter((b) => !state.shown_badges.includes(b.id));
  },
};
